import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserBalance } from '../utils/firebase/balance';
import type { UserBalance } from '../../types/user';

interface BalanceContextType {
  balance: UserBalance | null;
  loading: boolean;
  error: string | null;
  refreshBalance: () => Promise<void>;
}

const BalanceContext = createContext<BalanceContextType>({
  balance: null,
  loading: true,
  error: null,
  refreshBalance: async () => {}
});

export function BalanceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState<UserBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = async () => {
    if (!user) {
      setBalance(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setError(null);
      const userBalance = await getUserBalance(user.uid);
      setBalance(userBalance);
    } catch (error: any) {
      console.error('Error fetching balance:', error);
      // Don't show permission errors to users, handle silently
      if (error?.code === 'permission-denied') {
        setBalance(null);
      } else {
        setError('Failed to load balance information');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [user]);

  const refreshBalance = async () => {
    setLoading(true);
    await fetchBalance();
  };

  return (
    <BalanceContext.Provider
      value={{ balance, loading, error, refreshBalance }}
    >
      {children}
    </BalanceContext.Provider>
  );
}

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};
